import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import SendIcon from '@mui/icons-material/Send';
import Alert from '@mui/material/Alert';

import { useNotification } from '../../providers/NotificationProvider';
import { SectionCard, SectionCardAnswer } from '../../components/shared/FormSectionWidgets';
import { useDraftAutoSave } from '../../hooks/useDraftAutoSave';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { InspectionFormsApi } from '../../api-generated/apis';
import { apiConfig } from '../../api-generated/apiConfig';
import type { InspectionFormResponse, SectionAnswerDto } from '../../api-generated/models';

const formsApi = new InspectionFormsApi(apiConfig);

// ─── Main Component ──────────────────────────────────────────────────────────

const OperatorFormPage: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { isOnline } = useOnlineStatus();

  const [answers, setAnswers] = useState<Map<string, SectionCardAnswer>>(new Map());
  const [hasStarted, setHasStarted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);

  // ─── Draft Auto-Save Hook ──────────────────────────────────────────

  const {
    restoreDraft,
    clearDraft,
    savePendingSubmission,
    getPendingSubmission,
    clearPendingSubmission,
    hasPendingSubmission,
  } = useDraftAutoSave({
    formInstanceId: formId,
    answers,
    enabled: hasStarted,
    onAutoSaveError: () => {
      notification.showWarning('Auto-save failed. Your progress may not be preserved.');
    },
  });

  // ─── Queries ─────────────────────────────────────────────────────────

  const formQuery = useQuery<InspectionFormResponse, Error>({
    queryKey: ['formInstance', formId],
    queryFn: () => formsApi.getFormById({ id: formId! }),
    enabled: !!formId,
  });

  const answersQuery = useQuery<SectionAnswerDto[], Error>({
    queryKey: ['formAnswers', formId],
    queryFn: async () => {
      const result = await formsApi.getAnswers({ id: formId! });
      return (Array.isArray(result) ? result : [result]) as SectionAnswerDto[];
    },
    enabled: !!formId && !!formQuery.data,
  });

  // ─── Mutations ───────────────────────────────────────────────────────

  const saveAnswersMutation = useMutation({
    mutationFn: (sectionAnswers: SectionAnswerDto[]) =>
      formsApi.saveAnswers({
        id: formId!,
        saveAnswersRequest: { answers: sectionAnswers },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formAnswers', formId] });
      queryClient.invalidateQueries({ queryKey: ['formInstance', formId] });
    },
  });

  // ─── Sorted sections ────────────────────────────────────────────────

  const sortedSections = useMemo(() => {
    if (!formQuery.data?.sections) return [];
    return [...formQuery.data.sections].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  }, [formQuery.data?.sections]);

  // ─── Restore saved answers from server or localStorage ─────────────

  useEffect(() => {
    if (formQuery.data && !draftRestored && formId) {
      // First try server answers
      if (answersQuery.data && answersQuery.data.length > 0) {
        const serverAnswers = new Map<string, SectionCardAnswer>();
        answersQuery.data.forEach((a: SectionAnswerDto) => {
          if (a.sectionId) {
            serverAnswers.set(a.sectionId, {
              sectionId: a.sectionId,
              sectionType: (a.sectionType as SectionCardAnswer['sectionType']) ?? 'TEXT_RESULT',
              statusValue: a.statusValue as SectionCardAnswer['statusValue'],
              numericValue: a.numericValue,
              textValue: a.textValue,
            });
          }
        });
        setAnswers(serverAnswers);
        if (serverAnswers.size > 0) setHasStarted(true);
      } else {
        // Fall back to localStorage draft
        const restored = restoreDraft();
        if (restored && restored.size > 0) {
          setAnswers(restored as Map<string, SectionCardAnswer>);
          setHasStarted(true);
        }
      }
      setDraftRestored(true);
    }
  }, [formQuery.data, answersQuery.data, draftRestored, formId, restoreDraft]);

  // ─── Handle answer changes ──────────────────────────────────────────

  const handleAnswerChange = useCallback(
    (answer: SectionCardAnswer) => {
      setAnswers((prev) => {
        const next = new Map(prev);
        next.set(answer.sectionId, answer);
        return next;
      });

      if (!hasStarted) setHasStarted(true);
    },
    [hasStarted]
  );

  // ─── Save answers to server ─────────────────────────────────────────

  const handleSave = useCallback(async () => {
    const answersArray = Array.from(answers.values());
    if (answersArray.length === 0) return;

    const sectionAnswers: SectionAnswerDto[] = answersArray.map((a) => ({
      sectionId: a.sectionId,
      sectionType: a.sectionType as SectionAnswerDto['sectionType'],
      statusValue: a.statusValue as SectionAnswerDto['statusValue'],
      numericValue: a.numericValue,
      textValue: a.textValue,
    }));

    try {
      await saveAnswersMutation.mutateAsync(sectionAnswers);
      notification.showSuccess('Answers saved');
    } catch (error) {
      if (!isOnline) {
        savePendingSubmission(answersArray as any);
        notification.showWarning('Saved locally. Will sync when online.');
      } else {
        notification.showError('Failed to save answers');
      }
    }
  }, [answers, saveAnswersMutation, notification, isOnline, savePendingSubmission]);

  // ─── Submit (complete) the form ─────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    if (!formId) return;
    setIsSubmitting(true);

    try {
      // Save all answers first
      const answersArray = Array.from(answers.values());
      const sectionAnswers: SectionAnswerDto[] = answersArray.map((a) => ({
        sectionId: a.sectionId,
        sectionType: a.sectionType as SectionAnswerDto['sectionType'],
        statusValue: a.statusValue as SectionAnswerDto['statusValue'],
        numericValue: a.numericValue,
        textValue: a.textValue,
      }));

      if (sectionAnswers.length > 0) {
        await saveAnswersMutation.mutateAsync(sectionAnswers);
      }

      // Then mark as completed
      await formsApi.updateFormStatus({
        id: formId,
        updateFormStatusRequest: { status: 'COMPLETED' as any },
      });

      clearDraft();
      clearPendingSubmission();
      notification.showSuccess('Form submitted successfully!');
      queryClient.invalidateQueries({ queryKey: ['formInstance', formId] });
    } catch (error: unknown) {
      if (!isOnline) {
        savePendingSubmission(Array.from(answers.values()) as any);
        notification.showWarning('Saved locally, pending sync.');
      } else {
        notification.showError('Failed to submit form. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [formId, answers, saveAnswersMutation, clearDraft, clearPendingSubmission, savePendingSubmission, notification, queryClient, isOnline]);

  // ─── Auto-retry pending submission when back online ─────────────────

  useEffect(() => {
    if (isOnline && hasPendingSubmission) {
      const pending = getPendingSubmission();
      if (pending && pending.length > 0) {
        const sectionAnswers: SectionAnswerDto[] = pending.map((a: any) => ({
          sectionId: a.sectionId,
          sectionType: a.sectionType,
          statusValue: a.statusValue,
          numericValue: a.numericValue,
          textValue: a.textValue,
        }));
        saveAnswersMutation.mutate(sectionAnswers, {
          onSuccess: () => {
            clearPendingSubmission();
            notification.showSuccess('Pending answers synced successfully.');
          },
        });
      }
    }
  }, [isOnline, hasPendingSubmission, getPendingSubmission, saveAnswersMutation, clearPendingSubmission, notification]);

  // ─── Render: Loading State ──────────────────────────────────────────

  if (formQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">Loading inspection form...</Typography>
      </Box>
    );
  }

  if (formQuery.isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Failed to load form: {formQuery.error?.message || 'Unknown error'}</Alert>
      </Box>
    );
  }

  if (!formId) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">No form ID provided.</Alert>
      </Box>
    );
  }

  const formInstance = formQuery.data;
  if (!formInstance) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">Form not found.</Alert>
      </Box>
    );
  }

  const isFormCompleted = formInstance.status === 'COMPLETED';
  const isFormCancelled = formInstance.status === 'CANCELLED';
  const canSubmit = answers.size > 0 && !isSubmitting && !isFormCompleted && !isFormCancelled;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 2 }, py: 2 }}>
      {/* Form Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>Inspection Form</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip
            label={`Status: ${formInstance.status}`}
            color={
              formInstance.status === 'COMPLETED' ? 'success'
                : formInstance.status === 'IN_PROGRESS' ? 'info'
                : formInstance.status === 'OVERDUE' ? 'error'
                : 'default'
            }
            size="small"
          />
          <Chip label={`Sections: ${sortedSections.length}`} variant="outlined" size="small" />
          {formInstance.dueAt && (
            <Chip
              label={`Due: ${new Date(formInstance.dueAt).toLocaleDateString()}`}
              variant="outlined"
              size="small"
            />
          )}
        </Stack>
      </Box>

      {/* Offline indicator */}
      {!isOnline && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You are currently offline. Your answers are being saved locally.
        </Alert>
      )}

      {/* Pending sync indicator */}
      {hasPendingSubmission && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Saved locally, pending sync. Will auto-submit when connectivity is restored.
        </Alert>
      )}

      {/* Sections */}
      {sortedSections.length === 0 ? (
        <Alert severity="info">This form has no sections to complete.</Alert>
      ) : (
        <>
          {sortedSections.map((section) => (
            <SectionCard
              key={section.id}
              sectionId={section.id!}
              question={section.question || ''}
              description={section.description}
              sectionType={(section.sectionType as SectionCardAnswer['sectionType']) ?? 'TEXT_RESULT'}
              index={section.index ?? 0}
              guidanceImageUrl={section.guidanceImageUrl}
              thresholds={section.thresholds as any}
              answer={section.id ? answers.get(section.id) : undefined}
              onAnswerChange={handleAnswerChange}
              disabled={isFormCompleted || isFormCancelled}
            />
          ))}

          {/* Action Buttons */}
          {!isFormCompleted && !isFormCancelled && (
            <Stack direction="row" spacing={2} sx={{ mt: 3, mb: 4, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                size="large"
                onClick={handleSave}
                disabled={answers.size === 0 || saveAnswersMutation.isPending}
              >
                {saveAnswersMutation.isPending ? 'Saving...' : 'Save Progress'}
              </Button>
              <Button
                variant="contained"
                size="large"
                color="primary"
                onClick={handleSubmit}
                disabled={!canSubmit}
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                sx={{ minWidth: 200 }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Form'}
              </Button>
            </Stack>
          )}

          {isFormCompleted && (
            <Alert severity="success" sx={{ mt: 3 }}>
              This form has been submitted successfully.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default OperatorFormPage;
